'use client'

import React from 'react'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { motion } from 'motion/react'
import { Media } from '@/payload-types'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'
import { useInView } from 'react-intersection-observer'
import Link from 'next/link'
import { getLink } from '@/utilities/getLink'

export function MediumImpactHeroClient({
  title,
  subtitle,
  media,
  links,
}: {
  title: string
  subtitle: string
  media: Media | null | undefined | string
  links: any[] | null | undefined
}) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
    },

    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  }

  return (
    <header className="relative h-[60vh] min-h-[500px] overflow-hidden bg-stone-50 pt-32">
      {/* Background Image */}
      <div className="absolute right-0 top-0 h-full w-2/3 overflow-hidden">
        <Image
          loader={imgproxyEnabled ? imgproxyLoader : undefined}
          src={getMediaUrl(media)}
          alt={title ?? ''}
          width={1600}
          height={800}
          priority
          className="h-full w-full object-cover"
        />
        <div className="bg-linear-to-r absolute inset-0 from-stone-50 from-10% to-transparent" />
      </div>

      {/* Content */}
      <div className="relative h-full">
        <div className="mx-auto h-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            className="flex h-full flex-col justify-center md:w-1/2"
          >
            <motion.div variants={itemVariants}>
              <motion.h1 className="text-shadow-lg/20 mb-6 font-serif text-4xl font-light tracking-wide text-black md:text-7xl">
                {title}
              </motion.h1>
              <motion.p
                variants={itemVariants}
                className="mb-8 text-lg font-light leading-relaxed text-black/90 md:text-xl"
              >
                {subtitle}
              </motion.p>
            </motion.div>
            {links && links.length > 0 && (
              <motion.div variants={itemVariants} className="flex flex-col gap-4 sm:flex-row">
                {links.map((link) => (
                  <Link
                    href={getLink(link.link)}
                    key={link.id}
                    aria-label={link.link.label}
                    className={`rounded-full border px-6 py-2 text-base font-light transition-all duration-300 ${link.link.appearance === 'outline-solid' ? ' text-black border-black hover:bg-white/80' : 'bg-black text-white border-black hover:bg-black/80'}`}
                  >
                    {link.link.label}
                  </Link>
                ))}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </header>
  )
}

export default MediumImpactHeroClient
